import { slugify, type CreateMealServiceRequest, type MealServiceDetail } from '@utazzo/shared';
import {
  UzAlert,
  UzButton,
  UzDateTimePicker,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTabs,
  UzTextarea,
  UzTooltip,
} from '@utazzo/ui';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useApproveMealService,
  useCreateMealService,
  useMealServiceDetail,
  useUpdateMealService,
} from '../use-meal-services';

import { PhotoGalleryField, type GalleryPhoto } from '@/components/photo-gallery';
import {
  CityPicker,
  CountryPicker,
  CuisinePicker,
  CurrencyPicker,
  SupplierPicker,
} from '@/components/pickers';
import { CatalogEditorSkeleton } from '@/components/skeletons';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useCurrenciesList } from '@/features/currencies/use-currencies';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';
import { useListReturn } from '@/hooks/use-list-return';

/**
 * Full-page MealService edit, in the shared catalog-editor mold (matches Hotel /
 * Activity / Transfer / Visa): slim top bar + read-only legacy metadata strip +
 * two tabs (Details / Content & media), each a main column of Cards plus a 320px
 * sidebar. A sellable dining experience - distinct from board-basis meal plans
 * (BB/HB/FB/AI).
 *
 *   /meal-services/new          mode=create
 *   /meal-services/:id/edit     mode=edit
 */

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const MEAL_KIND_OPTIONS = [
  { value: 'BREAKFAST', label: 'Breakfast' },
  { value: 'BRUNCH', label: 'Brunch' },
  { value: 'LUNCH', label: 'Lunch' },
  { value: 'HIGH_TEA', label: 'High tea' },
  { value: 'DINNER', label: 'Dinner' },
  { value: 'SNACKS', label: 'Snacks' },
];

const SEATING_OPTIONS = [
  { value: 'SHARED', label: 'Shared' },
  { value: 'PRIVATE', label: 'Private' },
  { value: 'EXCLUSIVE', label: 'Exclusive (buyout)' },
];

const MARKUP_OPTIONS = [
  { value: '', label: 'No markup' },
  { value: 'PERCENT', label: 'Percent' },
  { value: 'FLAT', label: 'Flat amount' },
];

interface MealServiceFormValues {
  status: MealServiceDetail['status'];
  legacyCode: string | null;
  slug: string | null;
  name: string;
  mealKind: MealServiceDetail['mealKind'];
  cuisineId: string | null;
  restaurantName: string | null;
  cityId: string | null;
  countryId: string | null;
  locationName: string;
  seatingType: MealServiceDetail['seatingType'];
  maxCovers: number;
  currencyId: string | null;
  costPerAdult: number;
  costPerChild: number;
  fixedCost: number;
  markupType: 'PERCENT' | 'FLAT' | null;
  markupValue: number;
  servesAlcohol: boolean;
  isVegetarianAvailable: boolean;
  isVeganAvailable: boolean;
  isHalalAvailable: boolean;
  dressCode: string | null;
  vendorSupplierId: string | null;
  shortDescription: string | null;
  longDescriptionHtml: string | null;
  inclusionsHtml: string | null;
  exclusionsHtml: string | null;
  termsHtml: string | null;
  cancellationHtml: string | null;
  validityFrom: string | null;
  validityTo: string | null;
  photos: GalleryPhoto[];
  metaTagName: string | null;
}

const CREATE_DEFAULTS: MealServiceFormValues = {
  status: 'DRAFT',
  legacyCode: null,
  slug: null,
  name: '',
  mealKind: 'LUNCH',
  cuisineId: null,
  restaurantName: null,
  cityId: null,
  countryId: null,
  locationName: '',
  seatingType: 'SHARED',
  maxCovers: 0,
  currencyId: null,
  costPerAdult: 0,
  costPerChild: 0,
  fixedCost: 0,
  markupType: null,
  markupValue: 0,
  servesAlcohol: false,
  isVegetarianAvailable: true,
  isVeganAvailable: false,
  isHalalAvailable: false,
  dressCode: null,
  vendorSupplierId: null,
  shortDescription: null,
  longDescriptionHtml: null,
  inclusionsHtml: null,
  exclusionsHtml: null,
  termsHtml: null,
  cancellationHtml: null,
  validityFrom: null,
  validityTo: null,
  photos: [],
  metaTagName: null,
};

const parsePhotos = (a: unknown): GalleryPhoto[] => {
  if (!Array.isArray(a)) return [];
  return a.filter(
    (p): p is GalleryPhoto =>
      Boolean(p) &&
      typeof p === 'object' &&
      (typeof (p as { url?: unknown }).url === 'string' ||
        typeof (p as { mediaId?: unknown }).mediaId === 'string'),
  );
};

const trimOrNull = (v: string | null | undefined): string | null => {
  if (v === null || v === undefined) return null;
  const t = v.trim();
  return t === '' ? null : t;
};

const toFormValues = (m: MealServiceDetail | null): MealServiceFormValues => {
  if (!m) return CREATE_DEFAULTS;
  return {
    status: m.status,
    legacyCode: m.legacyCode,
    slug: m.slug,
    name: m.name,
    mealKind: m.mealKind,
    cuisineId: m.cuisineId,
    restaurantName: m.restaurantName,
    cityId: m.cityId,
    countryId: m.countryId,
    locationName: m.locationName ?? '',
    seatingType: m.seatingType,
    maxCovers: m.maxCovers,
    currencyId: m.currencyId,
    costPerAdult: m.costPerAdult,
    costPerChild: m.costPerChild,
    fixedCost: m.fixedCost,
    markupType: m.markupType === 'PERCENT' || m.markupType === 'FLAT' ? m.markupType : null,
    markupValue: m.markupValue,
    servesAlcohol: m.servesAlcohol,
    isVegetarianAvailable: m.isVegetarianAvailable,
    isVeganAvailable: m.isVeganAvailable,
    isHalalAvailable: m.isHalalAvailable,
    dressCode: m.dressCode,
    vendorSupplierId: m.vendorSupplierId,
    shortDescription: m.shortDescription,
    longDescriptionHtml: m.longDescriptionHtml,
    inclusionsHtml: m.inclusionsHtml,
    exclusionsHtml: m.exclusionsHtml,
    termsHtml: m.termsHtml,
    cancellationHtml: m.cancellationHtml,
    validityFrom: m.validityFrom,
    validityTo: m.validityTo,
    photos: parsePhotos(m.photos),
    metaTagName: m.metaTagName,
  };
};

const fromFormValues = (values: MealServiceFormValues): CreateMealServiceRequest => ({
  status: values.status,
  legacyCode: trimOrNull(values.legacyCode),
  slug: trimOrNull(values.slug),
  name: values.name.trim(),
  mealKind: values.mealKind,
  cuisineId: values.cuisineId ?? null,
  restaurantName: trimOrNull(values.restaurantName),
  cityId: values.cityId ?? null,
  countryId: values.countryId ?? null,
  locationName: values.locationName.trim() || null,
  seatingType: values.seatingType,
  maxCovers: values.maxCovers,
  currencyId: values.currencyId ?? null,
  costPerAdult: values.costPerAdult || 0,
  costPerChild: values.costPerChild || 0,
  fixedCost: values.fixedCost || 0,
  markupType: values.markupType,
  markupValue: values.markupValue || 0,
  servesAlcohol: values.servesAlcohol,
  isVegetarianAvailable: values.isVegetarianAvailable,
  isVeganAvailable: values.isVeganAvailable,
  isHalalAvailable: values.isHalalAvailable,
  dressCode: trimOrNull(values.dressCode),
  vendorSupplierId: values.vendorSupplierId ?? null,
  shortDescription: trimOrNull(values.shortDescription),
  longDescriptionHtml: trimOrNull(values.longDescriptionHtml),
  inclusionsHtml: trimOrNull(values.inclusionsHtml),
  exclusionsHtml: trimOrNull(values.exclusionsHtml),
  termsHtml: trimOrNull(values.termsHtml),
  cancellationHtml: trimOrNull(values.cancellationHtml),
  validityFrom: values.validityFrom || null,
  validityTo: values.validityTo || null,
  photos: values.photos.length > 0 ? values.photos : null,
  metaTagName: trimOrNull(values.metaTagName),
});

/** "YYYY-MM-DD" -> local Date (midnight), for UzDateTimePicker. */
const ymdToDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
/** Date -> "YYYY-MM-DD" (local). */
const dateToYmd = (d: Date | null): string =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : '';

/** Shopify-style card: title header + stacked fields. */
const Card = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): React.JSX.Element => (
  <section className="rounded-lg border border-zinc-200 bg-white">
    <header className="border-b border-zinc-100 px-5 py-3.5">
      <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
    </header>
    <div className="space-y-5 px-5 py-5">{children}</div>
  </section>
);

export const MealServiceEditPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backToList = useListReturn('/meal-services');
  const me = useCurrentUser();
  const canApprove = me?.role === 'ADMIN';
  const isCreate = !id || id === 'new';
  const [activeTab, setActiveTab] = useState<string>('details');

  const detailQuery = useMealServiceDetail(isCreate ? null : (id ?? null));
  const svc = detailQuery.data ?? null;

  const createSvc = useCreateMealService();
  const updateSvc = useUpdateMealService();
  const approveSvc = useApproveMealService();
  const pending = createSvc.isPending || updateSvc.isPending || approveSvc.isPending;

  const form = useForm<MealServiceFormValues>({ defaultValues: toFormValues(null) });

  const currenciesQuery = useCurrenciesList({ page: 1, pageSize: 200, sortDir: 'asc' });

  useEffect(() => {
    if (isCreate) form.reset(toFormValues(null));
    else if (svc) form.reset(toFormValues(svc));
  }, [isCreate, svc, form]);

  // Default the currency to INR on a fresh meal service once the list loads.
  useEffect(() => {
    if (!isCreate) return;
    if (form.getValues('currencyId')) return;
    const inr = currenciesQuery.data?.data.find((c) => c.code === 'INR');
    if (inr) form.setValue('currencyId', inr.id);
  }, [isCreate, currenciesQuery.data, form]);

  const submit = form.handleSubmit(async (values) => {
    if (!values.slug || !values.slug.trim()) {
      form.setError('slug', { message: 'URL slug is required (use the wand to generate)' });
      setActiveTab('details');
      return;
    }
    if (!values.countryId) {
      form.setError('countryId', { message: 'Country is required' });
      setActiveTab('details');
      return;
    }
    if (!values.cityId) {
      form.setError('cityId', { message: 'City is required' });
      setActiveTab('details');
      return;
    }
    const payload = fromFormValues(values);
    if (isCreate) {
      const created = await createSvc.mutateAsync(payload);
      navigate(`/meal-services/${created.id}/edit`);
    } else if (svc) {
      await updateSvc.mutateAsync({ id: svc.id, payload });
    }
  });

  const tabs = useMemo(() => {
    const list: { value: string; label: ReactNode; content: ReactNode }[] = [
      {
        value: 'details',
        label: 'Details',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="min-w-0 space-y-6">
                <IdentityCard form={form} pending={pending} />
                <LocationCard form={form} pending={pending} />
                <SeatingCard pending={pending} />
                <PricingCard form={form} pending={pending} />
              </div>
              <aside className="space-y-6 self-start lg:sticky lg:top-8">
                <StatusCard
                  pending={pending}
                  svc={svc}
                  canApprove={canApprove}
                  onToggleApprove={(tier, approve) =>
                    svc && approveSvc.mutate({ id: svc.id, payload: { tier, approve } })
                  }
                />
                <CommercialCard pending={pending} />
              </aside>
            </div>
          </UzForm>
        ),
      },
      {
        value: 'content',
        label: 'Content & media',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="mx-auto max-w-3xl space-y-6">
              <ContentCard pending={pending} />
              <MediaCard pending={pending} />
            </div>
          </UzForm>
        ),
      },
    ];
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- submit stable per render
  }, [isCreate, svc, canApprove, form, pending, approveSvc]);

  const statusDot =
    svc?.status === 'ACTIVE'
      ? 'bg-emerald-500'
      : svc?.status === 'DRAFT' || svc?.status === 'PENDING_APPROVAL'
        ? 'bg-amber-500'
        : 'bg-zinc-400';
  const statusText =
    svc?.status === 'ACTIVE'
      ? 'Active'
      : svc?.status === 'DRAFT'
        ? 'Draft'
        : svc?.status === 'PENDING_APPROVAL'
          ? 'Pending approval'
          : svc?.status === 'INACTIVE'
            ? 'Inactive'
            : svc?.status === 'ARCHIVED'
              ? 'Archived'
              : '';

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      {/* Sticky top bar - breaks out of the scroll container's padding so it sits
          flush under the app header and stays solid (content never shows through). */}
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to meal services"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Meal services</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {isCreate ? 'Add meal service' : svc ? svc.name : 'Edit'}
          </span>
        </div>
        <span className="flex-1" />
        {!isCreate && svc && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + statusDot} aria-hidden />
            {statusText}
          </span>
        )}
        <UzButton variant="ghost" size="sm" onClick={backToList} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending}>
          <span>{isCreate ? 'Create' : 'Save'}</span>
          <kbd className="ml-2 hidden h-4 items-center rounded border border-zinc-300/60 bg-white/15 px-1 font-mono text-[10px] text-current/80 sm:inline-flex">
            ⌘S
          </kbd>
        </UzButton>
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {!isCreate && svc && (
          <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
            {svc.isApprovedByAdmin && (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <span className="h-1 w-1 rounded-full bg-emerald-500" aria-hidden />
                Admin approved
              </span>
            )}
            {svc.isApprovedByModerator && <span>Moderator approved</span>}
            {svc.legacyId !== null && (
              <span>
                Legacy <span className="font-mono text-zinc-700 tabular-nums">#{svc.legacyId}</span>
              </span>
            )}
            {svc.legacyCode && (
              <span>
                Code <span className="font-mono text-zinc-700">{svc.legacyCode}</span>
              </span>
            )}
          </div>
        )}

        {!isCreate && detailQuery.isLoading && <CatalogEditorSkeleton />}
        {!isCreate && detailQuery.isError && (
          <UzAlert tone="destructive">
            Failed to load meal service detail. Go back and retry.
          </UzAlert>
        )}

        {(isCreate || svc) && <UzTabs value={activeTab} onValueChange={setActiveTab} tabs={tabs} />}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

interface CardProps {
  pending: boolean;
}
interface FormCardProps extends CardProps {
  form: UseFormReturn<MealServiceFormValues>;
}

const IdentityCard = ({ form, pending }: FormCardProps): React.JSX.Element => (
  <Card title="Identity">
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Service name"
          placeholder="e.g. Dinner at Hakkasan - Set menu"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="slug">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="URL slug"
          required
          error={error}
          disabled={pending}
          rightAdornment={
            <UzTooltip label="Auto-generate slug">
              <button
                type="button"
                disabled={pending}
                aria-label="Auto-generate slug"
                onClick={() => {
                  const name = (form.getValues('name') ?? '').trim();
                  if (name) field.onChange(slugify(name));
                }}
                className="text-zinc-400 transition-colors hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
              </button>
            </UzTooltip>
          }
        />
      )}
    </UzFormField>
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="mealKind">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Meal kind"
            options={MEAL_KIND_OPTIONS}
            value={(field.value as string | undefined) || 'LUNCH'}
            onValueChange={(v) => field.onChange(v)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="restaurantName">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Restaurant name"
            placeholder="e.g. Hakkasan Mumbai"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
    <UzFormField name="cuisineId">
      {({ field, error }) => (
        <CuisinePicker
          value={(field.value as string | null | undefined) ?? null}
          onValueChange={(v) => field.onChange(v)}
          disabled={pending}
          error={error}
        />
      )}
    </UzFormField>
  </Card>
);

const LocationCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const countryId = form.watch('countryId');
  return (
    <Card title="Location & validity">
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="countryId">
          {({ field, error }) => (
            <CountryPicker
              value={(field.value as string | null | undefined) ?? null}
              onValueChange={(v) => {
                field.onChange(v);
                if (v !== countryId) form.setValue('cityId', null);
              }}
              required
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        <UzFormField name="cityId">
          {({ field, error }) => (
            <CityPicker
              value={(field.value as string | null | undefined) ?? null}
              onValueChange={(v) => field.onChange(v)}
              countryId={countryId ?? null}
              required
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
      </div>
      <UzFormField name="locationName">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Venue / location"
            placeholder="e.g. hotel-attached restaurant"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="validityFrom">
          {({ field, error }) => (
            <UzDateTimePicker
              mode="date"
              displayFormat="dd/MM/yyyy"
              placeholder="dd/mm/yyyy"
              label="Quote valid from"
              value={ymdToDate(field.value as string | null | undefined)}
              onValueChange={(d) => field.onChange(dateToYmd(d) || null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="validityTo">
          {({ field, error }) => (
            <UzDateTimePicker
              mode="date"
              displayFormat="dd/MM/yyyy"
              placeholder="dd/mm/yyyy"
              label="Quote valid to"
              value={ymdToDate(field.value as string | null | undefined)}
              onValueChange={(d) => field.onChange(dateToYmd(d) || null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
    </Card>
  );
};

const SeatingCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Seating & dietary">
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="seatingType">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Seating type"
            options={SEATING_OPTIONS}
            value={(field.value as string | undefined) || 'SHARED'}
            onValueChange={(v) => field.onChange(v)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="maxCovers">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Max covers"
            precision={0}
            min={0}
            max={1000}
            value={field.value === null || field.value === undefined ? 0 : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 0)}
            hint="Used for exclusive buyout pricing & availability"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
    <UzFormField name="dressCode">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Dress code"
          placeholder="Smart casual | Formal | No restriction"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="servesAlcohol">
      {({ field }) => (
        <UzSwitch
          label="Serves alcohol"
          description="Used in pre-trip cues for dry destinations."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isVegetarianAvailable">
      {({ field }) => (
        <UzSwitch
          label="Vegetarian available"
          description="A vegetarian menu or option is offered."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isVeganAvailable">
      {({ field }) => (
        <UzSwitch
          label="Vegan available"
          description="Vegan menu / option is offered."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isHalalAvailable">
      {({ field }) => (
        <UzSwitch
          label="Halal available"
          description="Halal certified or option offered."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

const PricingCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const markupType = form.watch('markupType');
  return (
    <Card title="Pricing" description="Per-cover net rates. Fixed cost is for exclusive buyouts.">
      <UzFormField name="currencyId">
        {({ field, error }) => (
          <CurrencyPicker
            value={(field.value as string | null | undefined) ?? null}
            onValueChange={(v) => field.onChange(v)}
            disabled={pending}
            error={error}
          />
        )}
      </UzFormField>
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="costPerAdult">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label="Cost per adult"
              precision={2}
              min={0}
              value={(field.value as number | undefined) || ''}
              onValueChange={(v) => field.onChange(v ?? 0)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="costPerChild">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label="Cost per child"
              precision={2}
              min={0}
              value={(field.value as number | undefined) || ''}
              onValueChange={(v) => field.onChange(v ?? 0)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
      <UzFormField name="fixedCost">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Fixed cost"
            precision={2}
            min={0}
            hint="Exclusive buyout - per-event price replaces per-cover. Leave empty for per-cover."
            value={(field.value as number | undefined) || ''}
            onValueChange={(v) => field.onChange(v ?? 0)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="markupType">
          {({ field, error }) => (
            <UzSelect
              name={field.name}
              label="Markup type"
              options={MARKUP_OPTIONS}
              value={(field.value as string | null | undefined) || ''}
              onValueChange={(v) => field.onChange(v === '' ? null : (v as 'PERCENT' | 'FLAT'))}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        {markupType && (
          <UzFormField name="markupValue">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                label={markupType === 'PERCENT' ? 'Markup %' : 'Markup amount'}
                precision={2}
                min={0}
                rightAdornment={
                  markupType === 'PERCENT' ? (
                    <span className="text-sm text-zinc-500">%</span>
                  ) : undefined
                }
                value={(field.value as number | undefined) || ''}
                onValueChange={(v) => field.onChange(v ?? 0)}
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>
        )}
      </div>
    </Card>
  );
};

// ----- Sidebar cards -----

interface StatusCardProps extends CardProps {
  svc: MealServiceDetail | null;
  canApprove: boolean;
  onToggleApprove: (tier: 'admin' | 'moderator', approve: boolean) => void;
}

const StatusCard = ({
  pending,
  svc,
  canApprove,
  onToggleApprove,
}: StatusCardProps): React.JSX.Element => (
  <Card title="Status">
    <UzFormField name="status">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Lifecycle"
          options={
            canApprove
              ? STATUS_OPTIONS
              : STATUS_OPTIONS.filter((o) => o.value === 'DRAFT' || o.value === 'PENDING_APPROVAL')
          }
          value={(field.value as string | undefined) || 'DRAFT'}
          onValueChange={(v) => field.onChange(v)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    {svc && canApprove && (
      <>
        <UzSwitch
          label={svc.isApprovedByAdmin ? 'Admin approved' : 'Admin not approved'}
          description="Admin approval is the gate to status=ACTIVE."
          checked={svc.isApprovedByAdmin}
          onCheckedChange={(checked) => onToggleApprove('admin', Boolean(checked))}
          disabled={pending}
        />
        <UzSwitch
          label={svc.isApprovedByModerator ? 'Moderator approved' : 'Moderator not approved'}
          description="Moderator sign-off is informational."
          checked={svc.isApprovedByModerator}
          onCheckedChange={(checked) => onToggleApprove('moderator', Boolean(checked))}
          disabled={pending}
        />
      </>
    )}
  </Card>
);

const CommercialCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Commercial">
    <UzFormField name="vendorSupplierId">
      {({ field, error }) => (
        <SupplierPicker
          value={(field.value as string | null | undefined) ?? null}
          onValueChange={(v) => field.onChange(v)}
          disabled={pending}
          error={error}
        />
      )}
    </UzFormField>
  </Card>
);

// ----- Content & media -----

const ContentCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Descriptions & policy">
    <UzFormField name="shortDescription">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Short description"
          placeholder="One-sentence summary for picker cards."
          rows={2}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="longDescriptionHtml">
      {({ field }) => (
        <RichTextEditor
          label="Long description"
          placeholder="Multi-paragraph description with formatting..."
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(html) => field.onChange(html === '' ? null : html)}
          disabled={pending}
        />
      )}
    </UzFormField>
    <div className="grid gap-5 md:grid-cols-2">
      <UzFormField name="inclusionsHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Inclusions copy"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="exclusionsHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Exclusions copy"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="termsHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Terms & conditions"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="cancellationHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Cancellation policy"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  </Card>
);

const MediaCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Photos">
    <UzFormField name="photos">
      {({ field }) => (
        <PhotoGalleryField
          photos={(field.value as GalleryPhoto[]) ?? []}
          onChange={(next) => field.onChange(next)}
          folder="meal-services"
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);
